import { nextTick, ref } from "vue";
import { formatModelNumber } from "@/utils/tools";
export const useSameProductItemHeight = ({
  productListRef = ref(null),
  loading = ref(false),
  isMouted = ref(false),
  currentPageProducts = ref([]),
}) => {
  let mutationObserverInstance = null;
  const __CLASS_NO_MARGIN__ = "no-margin";

  // 把 productlist 里视觉上同一行的productItem归到一组
  const groupByRow = ($list) => {
    const map = {};
    $list.each(function () {
      const top = Math.round($(this).offset().top);
      (map[top] = map[top] || []).push(this);
    });
    return Object.keys(map)
      .sort(function (a, b) {
        return a - b;
      })
      .map(function (k) {
        return $(map[k]);
      });
  };

  // 把product按行分组
  const getRows = () => {
    return groupByRow($(productListRef.value).find(".csr-product-v1"));
  };

  // 获取同一行product对应区域的dom
  const getDom = ($row) => {
    return {
      $btn: $row.find(".csr-product-v1__btns"),
      $energy: $row.find(".csr-product-v1-energyLabel"),
      $price: $row.find(".csr-cp-price-v1__styles"),
      $priceBox: $row.find(".csr-product-v1__prices"),
      $shipping: $row.find(".csr-product-v1__prices-shipping"),
    };
  };

  // 获取同一行product对应区域的dom的高度
  const getDomHeight = ($el) => {
    return $el.map((_, el) => $(el).outerHeight(false)).get();
  };

  // 计算同一行需要等高区域的最高高度
  const getDomMaxHeight = (maxHeightObj) => {
    // eslint-disable-next-line
    for (const maxHeight in maxHeightObj) {
      maxHeightObj[maxHeight] = Math.max(
        ...getDomHeight(maxHeightObj[maxHeight])
      );
    }
    return maxHeightObj;
  };

  // pc端 每一行价格区域等高对齐
  const setRowPriceSameHeight = (needRequestAnimationFrame = true) => {
    loading.value = true;

    const resetHeight = () => {
      const rows = getRows();
      rows?.forEach(($row) => {
        const { $btn, $energy, $price, $priceBox, $shipping } = getDom($row);
        $btn.css("height", "auto");
        $energy.css("height", "auto");
        $price.css("height", "auto");
        $priceBox.css("height", "auto");
        $shipping.css("height", "auto");
        $btn.removeClass(__CLASS_NO_MARGIN__);
        $priceBox.removeClass(__CLASS_NO_MARGIN__);
        $shipping.removeClass(__CLASS_NO_MARGIN__);
      });
    };

    const setHeight = () => {
      const rows = getRows();
      rows?.forEach(($row) => {
        const { $btn, $energy, $price, $priceBox, $shipping } = getDom($row);
        const {
          maxBtnHeight,
          maxEnergyHeight,
          maxPriceHeight,
          maxPriceBoxHeight,
          maxShippingHeight,
        } = getDomMaxHeight({
          maxBtnHeight: $btn,
          maxEnergyHeight: $energy,
          maxPriceHeight: $price,
          maxPriceBoxHeight: $priceBox,
          maxShippingHeight: $shipping,
        });
        // 情况一：最高高度都为0，去除多余margin
        maxBtnHeight === 0 && $btn.addClass(__CLASS_NO_MARGIN__);
        maxPriceBoxHeight === 0 && $priceBox.addClass(__CLASS_NO_MARGIN__);
        // 整行都无包邮标签时，去掉占位容器自带的margin-top，避免多出空白
        maxShippingHeight === 0 && $shipping.addClass(__CLASS_NO_MARGIN__);

        // 情况二：高度不为0，等高处理
        maxBtnHeight !== 0 && $btn.css("height", `${maxBtnHeight}px`);
        maxEnergyHeight !== 0 && $energy.css("height", `${maxEnergyHeight}px`);
        maxPriceHeight !== 0 && $price.css("height", `${maxPriceHeight}px`);
        maxShippingHeight !== 0 &&
          $shipping.css("height", `${maxShippingHeight}px`);
      });

      // 等待渲染后再切换loading状态
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            loading.value = false;
          }, 300);
        });
      });
    };

    const doSetHeight = () => {
      if (needRequestAnimationFrame) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setHeight();
          });
        });
      } else {
        setHeight();
      }
    };

    // 空元素去除多余margin
    const handleEmptyDom = (dom) => {
      if ($(dom).is(":empty") || $(dom).text().trim() === "") {
        $(dom).addClass(__CLASS_NO_MARGIN__);
        resetHeight();
        doSetHeight();
      }
    };

    // 1. filter后不能拿到所有页面上的卡片，开启dom变化监听器兜底
    const mutationObserver = () => {
      if (!mutationObserverInstance) {
        mutationObserverInstance = new MutationObserver(function () {
          resetHeight();
          doSetHeight();
        });
        mutationObserverInstance.observe(productListRef.value, {
          childList: true,
          subtree: true,
        });
      }
    };

    // 2. 由于图片异步加载，可能在一次获取dom高度中不能获取最终渲染后的高度，需要监听元素高度变化来触发等高操作
    const resizeObserver = () => {
      const ro = new ResizeObserver(() => {
        resetHeight();
        doSetHeight();
      });
      const rows = getRows();
      rows?.forEach(($row) => {
        const { $energy } = getDom($row);
        $energy.find(".csr-product-v1-energyLabel-item").each((_, item) => {
          ro.observe(item);
        });
      });
    };

    // 3. 一些空元素间距处理
    const otherProcess = () => {
      // price 内部有些空元素附带margin，影响高度
      const rows = getRows();
      rows?.forEach(($row) => {
        const { $priceBox } = getDom($row);
        $priceBox.find(".csr-cp-price-v1 > div").each((_, item) => {
          handleEmptyDom(item);
        });
        $priceBox.find("> div").each((_, item) => {
          handleEmptyDom(item);
        });
      });
    };

    nextTick(() => {
      resetHeight();
      doSetHeight();
      mutationObserver();
      resizeObserver();
      otherProcess();
    });
  };

  // 计算当前页所有sku中，配置modelNumber重复的个数
  const countRepeatModelNumberSku = (currentPageAllSkus) => {
    let repeatSkuCount = 0;
    const noRepeatModels = [];
    currentPageAllSkus.forEach((e) => {
      if (
        !noRepeatModels.find(
          (item) =>
            formatModelNumber(item) ===
            formatModelNumber(e.product.productModelNumber)
        )
      ) {
        noRepeatModels.push(e.product.productModelNumber);
      } else {
        repeatSkuCount += 1;
      }
    });
    return repeatSkuCount;
  };

  // pc端 每一行productItem底部区域等高对齐
  const setRowContentSameHeight = () => {
    const event = "price-js-loaded";
    let skuList = [];
    const currentPageAllSkus = currentPageProducts.value.reduce((pre, cur) => {
      const curSkus = cur.sku.map((skuItem) => skuItem);
      return pre.concat(curSkus);
    }, []);
    const repeatSkuLength = countRepeatModelNumberSku(currentPageAllSkus);
    const { token } =
      window?.observer?.subscribe(event, ({ productModelNumber }) => {
        const sku = currentPageAllSkus.find(
          (skuItem) =>
            formatModelNumber(skuItem.product.productModelNumber) ===
            formatModelNumber(productModelNumber)
        );
        if (
          sku &&
          !skuList.find(
            (skuItem) =>
              formatModelNumber(skuItem.product.productModelNumber) ===
              formatModelNumber(sku.product.productModelNumber)
          )
        ) {
          skuList.push(sku);
        }
        // 因为配了相同的productModelNumber，需要把所有相同的productModelNumber的个数减出来
        if (currentPageAllSkus.length - repeatSkuLength === skuList.length) {
          setRowPriceSameHeight();
          isMouted.value = true;
          skuList = [];
          window?.observer?.unSubscribe(token, event);
        }
      }) ?? {};
  };

  return {
    setRowContentSameHeight,
    setRowPriceSameHeight,
  };
};
